'use no memo';
import {useMemo} from 'react';
import {useAppSelector} from '@/hooks';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import {SelectionType} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {getPageItemTransformVersion} from '@Components/poster-editor/poster-session-reducer';

type ItemUid = string;

export function useGetFabricObjectPropertiesMap<T extends keyof FabricObject>(
  pageHashedId: string,
  keys: T[],
  selectionType: SelectionType
): Record<ItemUid, Pick<FabricObject, T>> | undefined {
  const hoveredItemId = useAppSelector((state) => selectionType === SelectionType.HOVERED_ITEM ? state.posterEditor.hoveredItemId[pageHashedId] : undefined);
  const marqueePreviewItemIds = useAppSelector((state) => selectionType === SelectionType.MARQUEE_PREVIEW ? state.posterEditor.marqueePreviewItemIds[pageHashedId] : undefined);
  const activeItemIds = useAppSelector((state) =>
    selectionType !== SelectionType.HOVERED_ITEM && selectionType !== SelectionType.MARQUEE_PREVIEW ? state.posterEditor.activeItemIds[pageHashedId] : undefined
  );

  // Commits publish a new itemsHashMap; in-progress gestures bump the transform version. Either
  // means the live Fabric values below may have moved, so both are inputs even though neither is read.
  const itemsHashMap = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.pages.pagesHashMap[pageHashedId]?.items?.itemsHashMap;
  });
  const itemTransformVersion = useAppSelector((state) => getPageItemTransformVersion(state, pageHashedId));

  return useMemo(() => {
    if (!itemsHashMap) {
      return undefined;
    }

    const whiteboard = window.posterEditor?.whiteboard;

    if (selectionType === SelectionType.HOVERED_ITEM && hoveredItemId) {
      const hoveredItem = whiteboard?.getItemForId(hoveredItemId);
      if (!hoveredItem) {
        return undefined;
      }

      const obj: Partial<Pick<FabricObject, T>> = {};
      for (const key of keys) {
        obj[key] = hoveredItem.fabricObject.get(key) as FabricObject[T];
      }

      return {[hoveredItem.uid]: obj as Pick<FabricObject, T>};
    }

    if (selectionType === SelectionType.MARQUEE_PREVIEW) {
      if (!marqueePreviewItemIds || marqueePreviewItemIds.length === 0) {
        return undefined;
      }
      const result: Record<ItemUid, Pick<FabricObject, T>> = {};
      for (const itemId of marqueePreviewItemIds) {
        const item = whiteboard?.getItemForId(itemId);
        if (!item) {
          continue;
        }
        const obj: Partial<Pick<FabricObject, T>> = {};
        for (const key of keys) {
          obj[key] = item.fabricObject.get(key) as FabricObject[T];
        }
        result[item.uid] = obj as Pick<FabricObject, T>;
      }
      return Object.keys(result).length > 0 ? result : undefined;
    }

    if (selectionType === SelectionType.MULTI_SELECTION) {
      const page = whiteboard?.pages.getPage(pageHashedId);
      const activeSelection = page?.activeSelection.getActiveObject();

      if (!(activeSelection instanceof ActiveSelection)) {
        return undefined;
      }

      const obj: Partial<Pick<FabricObject, T>> = {};
      for (const key of keys) {
        obj[key] = activeSelection.get(key) as FabricObject[T];
      }

      return {'multi-selection': obj as Pick<FabricObject, T>};
    }

    if (!activeItemIds || activeItemIds.length === 0) {
      return undefined;
    }

    const result: Record<ItemUid, Pick<FabricObject, T>> = {};
    for (const itemId of activeItemIds) {
      const item = whiteboard?.getItemForId(itemId);
      if (!item) {
        return undefined;
      }

      const obj: Partial<Pick<FabricObject, T>> = {};
      for (const key of keys) {
        obj[key] = item.fabricObject.get(key) as FabricObject[T];
      }

      result[item.uid] = obj as Pick<FabricObject, T>;
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }, [hoveredItemId, marqueePreviewItemIds, activeItemIds, itemsHashMap, itemTransformVersion, pageHashedId, keys, selectionType]);
}
